import _ from 'lodash'
import { useCallback, useMemo } from 'react'
import { convertEmissionValue } from 'app/MissionControlMethaneSolution/helpers/calculations'
import { formatEmissionValue } from 'app/MissionControlMethaneSolution/helpers/emissionEvent'
import { useEmissionUnitsPreference } from '../emissionEvent/useEmissionUnitsPreference'

export type EmissionValueUnitFormat = {
  emissionWithUnit: string
  emissionUnitLabel: string
  emissionValue: string | number
}

export type FormatEmissionValueParams = {
  value?: number | null
  isTotalValue?: boolean
  enableFormat?: boolean
}

export type UseEmissionValueFormatter = {
  convertAndFormatEmissionValue: (
    params: FormatEmissionValueParams
  ) => EmissionValueUnitFormat
  unitConfig: ReturnType<typeof useEmissionUnitsPreference>
}

export const useEmissionValueFormatter = (): UseEmissionValueFormatter => {
  const unitConfig = useEmissionUnitsPreference()

  const convertAndFormatEmissionValue = useCallback(
    ({
      value,
      isTotalValue,
      enableFormat = true,
    }: FormatEmissionValueParams) => {
      const { value: convertedValue, unit } = convertEmissionValue(value, {
        ...unitConfig,
        isTotal: isTotalValue,
      })

      const emissionValue = enableFormat
        ? formatEmissionValue({
            value: convertedValue,
            isVolumeMode: isTotalValue,
          })
        : convertedValue

      const emissionWithUnit = _.compact([emissionValue, unit]).join(' ')

      return {
        emissionValue: emissionValue as string | number,
        emissionWithUnit,
        emissionUnitLabel: unit || '',
      }
    },
    [unitConfig]
  )

  return {
    convertAndFormatEmissionValue,
    unitConfig,
  }
}

/** Converts emission values between different unit systems and time periods */
export const useEmissionValueUnitConversion = ({
  value,
  isTotal,
  enableFormat,
}: {
  value?: number | null
  isTotal?: boolean
  enableFormat?: boolean
}): EmissionValueUnitFormat => {
  const { convertAndFormatEmissionValue } = useEmissionValueFormatter()

  return useMemo(() => {
    return convertAndFormatEmissionValue({
      value,
      isTotalValue: isTotal,
      enableFormat,
    })
  }, [convertAndFormatEmissionValue, isTotal, value, enableFormat])
}

export type EmissionRateUnitFormat = {
  emissionRateWithUnit: EmissionValueUnitFormat['emissionWithUnit']
  emissionRateValue: EmissionValueUnitFormat['emissionValue']
  emissionRateUnitLabel: EmissionValueUnitFormat['emissionUnitLabel']
}

const useEmissionRateUnitConversion = (
  emissionsRate?: number | null
): EmissionRateUnitFormat => {
  const { emissionWithUnit, emissionValue, emissionUnitLabel } =
    useEmissionValueUnitConversion({ value: emissionsRate })

  return {
    emissionRateWithUnit: emissionWithUnit,
    emissionRateValue: emissionValue,
    emissionRateUnitLabel: emissionUnitLabel,
  }
}

export default useEmissionRateUnitConversion
